import { Button, Icon16Checkmark, Typography } from "@cur8/maneki";
import { useClearRoomControllerMutation } from "hooks/mutations/useClearRoomControllerMutation";
import { useBloodWorkModeQuery } from "hooks/queries/useBloodWorkModeQuery";
import { useECGDeviceQuery } from "hooks/queries/useECGDeviceQuery";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useRoomQuery } from "hooks/queries/useRoomQuery";
import { useVisitBloodSamplesQuery } from "hooks/queries/useVisitBloodSamplesQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { formatTimeRange } from "lib/formatDateTime";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";
import { ecgScanLabel } from "lib/ecgScanLabel";
import { outstandingExaminations } from "lib/outstandingExaminations";
import { CheckInParams } from "lib/types/checkInParameters";
import { Fragment, useState } from "react";
import { Checkbox } from "react-aria-components";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { AppointmentCard } from "render/ui/presentation/AppointmentCard";
import { Banner } from "render/ui/presentation/Banner";
import styles from "./styles.module.sass";

// Checkout used to take a single press with no confirmation and no way back, so
// an operator hit it mid-visit during US testing and a later scan could not be
// attributed to the member (AB#92438).
//
// It no longer mirrors SelectVisitConfirm: sharing that treatment let operators
// dismiss it as the routine check-in confirmation, so it carries a caution
// banner and states the outcome in its title (AB#102209). The button names the
// operator's own action. Not "Begin the debrief" or anything reading as
// starting one: the operator does not run the debrief and will hesitate over a
// button implying they do.
//
// The screen used to assert "All assessments are complete" whatever the visit
// actually said, which is the one claim an operator would lean on to skip
// checking. It now reads the same scan states as the list behind it and names
// what is not marked complete, since finishing is unrecoverable.
//
// Open assessments gate the button behind an acknowledgement rather than
// blocking it: the badge can lag the room, so an operator who can see the scan
// is done still needs a way out, but they have to tick that they know what is
// being left unrecorded first.
//
// With assessments open the consequence sits in the supporting copy and the
// caution card carries only the list and the tick, so the operator reads what
// finishing costs before reaching the thing they have to agree to. With
// everything complete there is no list, so the card carries the consequence
// itself and the wording stays as the story specifies it.
//
// Only the scan-progress screen reaches this, and it runs exactly what that
// button ran before: clear-room-controller, nothing else. Deliberately not
// complete-visit, which the backend rejects for base scans. See #88682.
export function CheckOutConfirmView({
  replaceWith,
  onBack,
  patientId,
  visitId,
}: {
  replaceWith(url: string): void;
  onBack(): void;
} & CheckInParams) {
  const { data: member } = usePatientQuery({ patientId });
  const visitQuery = useVisitQuery({ patientId, visitId });
  const visit = visitQuery.data;
  const { data: room } = useRoomQuery({ roomId: visit?.roomId });
  const { data: visitHistory } = useVisitHistoryQuery({ patientId });

  // Filtered rather than interpolated: the sibling check-in screen renders
  // "undefined undefined" for the frame before the member query lands.
  const fullName = [member?.name?.firstName, member?.name?.lastName]
    .filter(Boolean)
    .join(" ");
  const countryCode = visit?.site.address.countryCode?.toUpperCase();
  const time =
    visit != null
      ? formatTimeRange(visit.startTime, visit.endTime, countryCode)
      : undefined;
  const visitTypeLabel =
    visit != null && visitHistory != null
      ? generateVisitTypeLabel({ visit, visitHistory })
      : undefined;

  const bloodSamplesQuery = useVisitBloodSamplesQuery({ patientId, visitId });
  const bloodWorkModeQuery = useBloodWorkModeQuery({ patientId, visitId });

  // Only names the ECG, so it does not gate the button: a label that arrives
  // late is not a claim about what is complete.
  const ecgDeviceQuery = useECGDeviceQuery({ patientId, visitId });

  const outstanding = outstandingExaminations({
    visit,
    bloodSamples: bloodSamplesQuery.data?.bloodSamples,
    bloodWorkMode: bloodWorkModeQuery.data?.bloodWorkMode,
    ecgLabel: ecgScanLabel(ecgDeviceQuery.data?.deviceType),
  });

  // Three sources decide what is open, and "no answer yet" is not the same as
  // "nothing open". Until they land, an empty list would have this screen
  // promise that every assessment is complete before it had looked at one.
  const sources = [visitQuery, bloodSamplesQuery, bloodWorkModeQuery];
  const checking = sources.some((query) => query.isPending);
  // A source that failed outright never answers, so the list cannot be trusted
  // to be empty. Say so and take the acknowledgement rather than blocking: an
  // operator left unable to release the room is worse than one told we could
  // not check.
  const unchecked = !checking && sources.some((query) => query.isError);

  const hasOpen = outstanding.length > 0;
  const one = outstanding.length === 1;
  const mustAcknowledge = hasOpen || unchecked;

  // Tied to the list it was given, not a bare yes. The queries refetch on
  // window focus, so an assessment can fail while the operator is looking
  // away, and a plain boolean would carry their agreement onto a longer list
  // than the one they read.
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | undefined>();
  // Keyed on what is open, not on how it is named. The ECG label depends on a
  // separate query, and keying on labels would drop the tick when that lands.
  const acknowledgement = outstanding.map(({ key }) => key).join("|");
  const acknowledged = acknowledgedFor === acknowledgement;

  const clearRoomController = useClearRoomControllerMutation();

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.title}>
              <Typography variant="display-s">Finish the scan?</Typography>
              <Typography variant="body-m" color="subtle">
                {checking
                  ? "Checking which assessments are complete."
                  : unchecked
                    ? "We could not check which assessments are complete. Finishing now will unassign the member from the room. No further assessments can be completed after this."
                    : hasOpen
                      ? `${outstanding.length} assessment${one ? "" : "s"} ${
                          one ? "remains" : "remain"
                        } open. Finishing now will leave ${
                          one ? "it" : "them"
                        } unassessed and unassign the member from the room. No further assessments can be completed after this.`
                      : "All assessments are complete. The member is ready to start the debrief."}
              </Typography>
            </div>

            {checking ? undefined : (
              <Banner
                kind="caution"
                size="l"
                detail={
                  mustAcknowledge ? (
                    <div className={styles.openDetail}>
                      <div className={styles.openDivider} />
                      <Checkbox
                        className={styles.acknowledge}
                        isSelected={acknowledged}
                        onChange={(isSelected) =>
                          setAcknowledgedFor(
                            isSelected ? acknowledgement : undefined
                          )
                        }
                      >
                        <div className={styles.acknowledgeBox}>
                          <Icon16Checkmark />
                        </div>
                        <Typography variant="body-m" as="span">
                          {unchecked
                            ? "I understand assessments may be left unrecorded."
                            : "I understand the open assessments above will not be recorded."}
                        </Typography>
                      </Checkbox>
                    </div>
                  ) : undefined
                }
              >
                {unchecked ? (
                  "Open assessments could not be checked."
                ) : hasOpen ? (
                  <>
                    Open:{" "}
                    {outstanding.map(({ key, label }, index) => (
                      <Fragment key={key}>
                        <strong>{label}</strong>
                        {index < outstanding.length - 1 ? ", " : "."}
                      </Fragment>
                    ))}
                  </>
                ) : (
                  "This unassigns the member from the room. No further assessments can be done after this."
                )}
              </Banner>
            )}

            <AppointmentCard
              topLeft={fullName}
              topRight={room?.displayName}
              bottomLeft={visitTypeLabel}
              bottomRight={time}
            />
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                onPress={() => {
                  clearRoomController.mutate(
                    { patientId, visitId },
                    {
                      // Replaces rather than pushes: once the room is
                      // unassigned this screen has nothing left to do, and a
                      // back-stack entry would let the operator return and
                      // press Check out a second time.
                      onSuccess: () =>
                        replaceWith(
                          paths.checkOutConfirmed.url({ patientId, visitId })
                        ),
                    }
                  );
                }}
                fillContainer
                state={
                  clearRoomController.isPending
                    ? "loading"
                    : checking || (mustAcknowledge && !acknowledged)
                      ? "disabled"
                      : undefined
                }
              >
                Finish the scan
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
